import { X } from "lucide-react";

/**
 * Right-side drawer overlay used across admin resource pages.
 * Renders on top of everything, closes on backdrop click or the X.
 *
 * Props:
 *   open, onClose          — visibility control
 *   title, subtitle        — header text; subtitle is the small eyebrow above the title
 *   testId                 — data-testid on the drawer body
 *   size                   — "sm" | "md" | "lg" | "xl" (max-width)
 *   onSave, saveLabel      — when onSave is provided, a right-aligned save button renders
 *                            in the footer
 *   saving                 — disables the save button + shows "Saving..."
 *   footer                 — node placed on the left of the footer (opposite the save button)
 *   children               — drawer body content (form fields, timeline, report, etc.)
 */
export default function ResourceDrawer({
  open, onClose,
  title, subtitle,
  testId,
  size = "md",
  onSave, saveLabel = "Save", saveTestId, saving = false,
  footer,
  children,
}) {
  if (!open) return null;
  const width = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-3xl",
  }[size] || "max-w-lg";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div
        className={`bg-white w-full ${width} h-full overflow-y-auto p-8`}
        onClick={e => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="flex items-start justify-between mb-6 gap-4">
          <div className="min-w-0">
            {subtitle && <div className="text-xs uppercase tracking-wider text-ccc-gold-dark">{subtitle}</div>}
            <h2 className="font-serif text-3xl text-ccc-plum leading-tight">{title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="text-stone-500 hover:text-ccc-plum shrink-0"
          >
            <X size={18}/>
          </button>
        </div>

        {children}

        {(onSave || footer) && (
          <div className="mt-6 flex items-center justify-between gap-4">
            <div className="text-sm text-stone-500">{footer}</div>
            {onSave && (
              <button
                onClick={onSave}
                disabled={saving}
                data-testid={saveTestId}
                className="bg-ccc-purple hover:bg-ccc-purple-dark text-white px-6 py-3 uppercase text-sm tracking-wider font-semibold disabled:opacity-60"
              >
                {saving ? "Saving..." : saveLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
